function cleanText(value) {
  if (value === null || value === undefined) {
    return null
  }

  const text = typeof value === 'string' ? value.trim() : String(value).trim()

  return text || null
}

function pickFirstText(...values) {
  for (const value of values) {
    const text = cleanText(value)

    if (text) {
      return text
    }
  }

  return undefined
}

function toArray(value) {
  if (!value) {
    return []
  }

  return Array.isArray(value) ? value : [value]
}

function normalizeMetaTag(tag) {
  if (!tag || typeof tag !== 'object') {
    return null
  }

  const normalized = { ...tag }

  if (normalized.name?.startsWith('og:') && !normalized.property) {
    normalized.property = normalized.name
    delete normalized.name
  }

  if ('content' in normalized) {
    const content = cleanText(normalized.content)

    if (!content) {
      return null
    }

    normalized.content = content
  }

  const hasHttpEquiv = normalized.httpEquiv || normalized['http-equiv']
  const hasMetaKey = normalized.name || normalized.property || normalized.charset || hasHttpEquiv
  const hasMetaValue = normalized.content || normalized.charset || hasHttpEquiv

  return hasMetaKey && hasMetaValue ? normalized : null
}

function normalizeLinkTag(tag) {
  if (!tag || typeof tag !== 'object') {
    return null
  }

  const href = cleanText(tag.href)

  if (!tag.rel || !href) {
    return null
  }

  return {
    ...tag,
    href,
  }
}

export function buildSeoMeta(seoData, fallback = {}) {
  const title = pickFirstText(seoData?.metaTitle, fallback.title)
  const description = pickFirstText(seoData?.metaDescription, fallback.description)
  const ogTitle = pickFirstText(seoData?.ogTitle, fallback.ogTitle, title)
  const ogDescription = pickFirstText(seoData?.ogDescription, fallback.ogDescription, description)
  const h1 = pickFirstText(seoData?.h1, fallback.h1)
  const seoText = pickFirstText(seoData?.seoText, fallback.seoText)
  const meta = {}

  if (title) {
    meta.title = title
  }
  if (description) {
    meta.description = description
  }
  if (ogTitle) {
    meta.ogTitle = ogTitle
  }
  if (ogDescription) {
    meta.ogDescription = ogDescription
  }
  if (h1) {
    meta.h1 = h1
  }
  if (seoText) {
    meta.seoText = seoText
  }

  return meta
}

export function buildHeadInput(seoMeta = {}, context = {}) {
  const head = {}
  const meta = []
  const link = []
  const script = toArray(context.scripts).filter(Boolean)

  if (seoMeta.title) {
    head.title = seoMeta.title
  }

  if (context.titleTemplate) {
    head.titleTemplate = context.titleTemplate
  }

  toArray(context.metaTags).forEach((tag) => {
    const normalized = normalizeMetaTag(tag)

    if (normalized) {
      meta.push(normalized)
    }
  })

  if (seoMeta.description) {
    meta.push({ name: 'description', content: seoMeta.description })
  }
  if (seoMeta.ogTitle) {
    meta.push({ property: 'og:title', content: seoMeta.ogTitle })
  }
  if (seoMeta.ogDescription) {
    meta.push({ property: 'og:description', content: seoMeta.ogDescription })
  }
  if (context.robots) {
    meta.push({ name: 'robots', content: context.robots })
  }

  toArray(context.links).forEach((tag) => {
    const normalized = normalizeLinkTag(tag)

    if (normalized) {
      link.push(normalized)
    }
  })

  const canonicalLink = cleanText(context.canonicalLink)

  if (canonicalLink) {
    link.push({
      key: 'canonical',
      rel: 'canonical',
      href: canonicalLink,
    })
  }

  toArray(context.alternateLinks).forEach((tag) => {
    const normalized = normalizeLinkTag(tag)

    if (normalized) {
      link.push(normalized)
    }
  })

  if (meta.length) {
    head.meta = meta
  }
  if (link.length) {
    head.link = link
  }
  if (script.length) {
    head.script = script
  }

  return head
}
